/**
 * ## AdvancedItemsProvider.tsx ##
 * This file contains AdvancedItemsProvider component
 * @packageDocumentation
 */

import React, { PropsWithChildren } from 'react';

import update from 'immutability-helper';

import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import {
	ItemsProvider,
	useItemsProviderContext,
	ItemsProviderProps,
	createItemsProviderContext,
	defaultTransformFiltersBeforeHandleUrl,
	SortingDirection,
	ItemsProviderContextActions,
	WithKey,
	ItemsProviderContext,
	ColumnFilter,
	List,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import { BaseParams } from '@common/typescript/objects/BaseParams';
import { getSearchParamsFromUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';

export type { BaseFilters } from '@common/react/components/Core/ItemsProvider/ItemsProvider';

/**
 * AdvancedItemsProvider component props
 *
 * @interface AdvancedItemsProviderProps
 * @typeParam T - T Any WithDeleted entity
 * @typeParam TFilter
 * @typeParam TResponse
 */
export interface AdvancedItemsProviderProps<T extends WithDeleted,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<T> = List<T>> extends ItemsProviderProps<T, TFilter, TResponse> {
		/**
		 * determines whether to add an item from addItem to the array based on filters
		 * @param item         - element being checked
		 * @param filters      - current filters
		 * @return Promise<boolean> | boolean
		 */
		filterHandler: (item: T, filters: TFilter) => boolean | Promise<boolean>;
		/**
		 * default sorting (when no sorting is specified in the filters)
		 */
		defaultSort?: [string, SortingDirection];
		/**
		 * defines the sort order of elements after adding a new element via addItem
		 * @param item1         - first element to be sorted
		 * @param item2         - second element to be sorted
		 * @param filters       - current filters
		 * @return number
		 */
		sortHandler?: (item1: T, item2: T, filters: TFilter) => number; // return number as function for Array.sort
		/**
		 * custom request name after changeOrder
		 */
		changeSortRequest?: string;
	}

interface AdvancedItemsHandlerProps<T extends WithKey, TFilter extends BaseParams = BaseParams> {
	filterHandler: (item: T, filters: TFilter) => Promise<boolean> | boolean;
	defaultSort?: [string, SortingDirection];
	sortHandler?: (item1: T, item2: T, filters: TFilter) => number;
	add?: (items: Array<T>) => T;
	addedFirst?: boolean;
	changeSortRequest?: string;
}

export interface AdvancedItemsProviderContextActions<T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<T> = List<T>> extends ItemsProviderContextActions<T, TFilter, TResponse> {
	saveSortOrder: (options: SaveSortOrderOptions<T>) => void;
}

export const checkFilterProp = (value: Array<any> | number | string | boolean | undefined | null) => {
	if (value === undefined || value === null || value === '') {
		return false;
	}
	if (typeof value === 'number') {
		return value > -1;
	}
	if (typeof value === 'string') {
		if (!isNaN(+value)) {
			return +value > -1;
		}
	}
	if (Array.isArray(value)) {
		return value.length > 0;
	}
	return true;
};

export const findId = <T, >(array: Array<T>, id: number | undefined): T => {
	return array.find((i) => +i === +(id || -1));
};

const defaultSortHandler = <T, TFilter extends { column?: Array<ColumnFilter> }>(
	item1: T,
	item2: T,
	filters: TFilter,
	defaultSort: [string, SortingDirection],
) => {
	const { column } = filters;
	if (column?.[0]) {
		const { caption: prop, direction } = column[0];
		if (direction === SortingDirection.Ascending) {
			return item1[prop] - item2[prop];
		}
		if (direction === SortingDirection.Descending) {
			return item2[prop] - item1[prop];
		}
	}
	const [prop, type] = defaultSort;
	if (type === SortingDirection.Ascending) {
		return item1[prop] - item2[prop];
	}
	if (type === SortingDirection.Descending) {
		return item2[prop] - item1[prop];
	}
	return 0;
};

interface SaveSortOrderOptions<T extends WithKey> {
	oldIndex: number;
	newIndex: number;
	dragIndexName?: string;
	noSave?: boolean;
	updateAfterDrag?: (item: T, items: Array<T>) => Promise<any>;
}

export const useAdvancedItemsProviderContext = <T extends WithKey,
	TFilters extends BaseParams = BaseParams,
	TResponse extends List<T> = List<T>,
>(required?: boolean) => useItemsProviderContext<T,
	TFilters, TResponse, AdvancedItemsProviderContextActions<T, TFilters, TResponse>>(required);

const AdvancedItemsHandler: <T extends WithDeleted, TFilter extends BaseParams = BaseParams, TResponse extends List<any> = List<T>>(
	p: PropsWithChildren<AdvancedItemsHandlerProps<T, TFilter>>
) => React.ReactElement = <T extends WithDeleted, TFilter extends BaseParams = BaseParams, TResponse extends List<any> = List<T>>(
	p: PropsWithChildren<AdvancedItemsHandlerProps<T, TFilter>>,
) => {
	const {
		children,
		filterHandler,
		sortHandler,
		defaultSort = ['sortOrder', SortingDirection.Ascending],
		changeSortRequest,
	} = p;
	const ItemsContext = createItemsProviderContext<T, TFilter, TResponse, AdvancedItemsProviderContextActions<T, TFilter, TResponse>>();

	const context = useItemsProviderContext<T, TFilter, TResponse>();

	const {
		state: {
			items: itemsProp, pagination, filters, loading, loaders, edits, errors, error, saveAllRequest,
		},
		actions: {
			setItems, reload, load, setEdit, setLoading, saveItems, save,
		},
	} = context;

	const [id, setId] = React.useState<number>(() => {
		if (itemsProp) {
			const minId = Math.min(...itemsProp.map((item) => item.id)) - 1;
			if (minId < 0) return minId;
		}
		return -1;
	});

	const value: ItemsProviderContext<T, TFilter, TResponse, AdvancedItemsProviderContextActions<T, TFilter, TResponse>> = React.useMemo(() => {
		const { pageSize, current } = pagination;

		const isFirstPage = current === 1;

		const items = itemsProp.slice(isFirstPage ? 0 : 1)
			.slice(0, pageSize - 2 + Object.keys(edits)
				.filter((key) => +key < 0).length);

		const handledPagination = {
			...(pagination || { current: 1, pageSize: 10, total: 0 }),
			pageSize: pagination.pageSize ? pagination.pageSize - 2 : 10,
		};

		const handleReload: ItemsProviderContext<T, TFilter, TResponse>['actions']['reload'] = (params, options) => {
			const pageSize = params?.pageSize || pagination.pageSize - 2;
			const current = (params && 'current' in params ? params?.current : undefined) || params?.page || pagination.current;
			const isFirstPage = current === 1;

			return reload(params ? {
				...params,
				pageSize: params?.pageSize ? params.pageSize + 2 : undefined,
				offset: isFirstPage ? 0 : pageSize * (current - 1) - 1,
			} : undefined, options);
		};

		const addItem = (item: T) => {
			Promise.resolve(filterHandler(item, filters)).then((res) => {
				if (res) {
					const newItems = sortHandler || defaultSort
						? itemsProp.concat(item)
							.sort((item1, item2) =>
								(sortHandler ? sortHandler(item1, item2, filters)
									: defaultSortHandler(item1, item2, filters, defaultSort)))
						: itemsProp.concat(item);
					if (newItems.length > pagination.pageSize) {
						newItems.pop();
					}
					setItems(newItems);
				}
			});
		};

		const saveSortOrder = (options: SaveSortOrderOptions<T>) => {
			const {
				oldIndex,
				newIndex,
				dragIndexName = 'sortOrder',
				noSave = false,
				updateAfterDrag,
			} = options;
			const minIndex = Math.max(Math.min(...itemsProp.map((item) => +item[dragIndexName])), 0);
			const useAsIndex = itemsProp.length - 1 === Math.abs((itemsProp.slice(0)[0]?.[dragIndexName] || 0)
				- itemsProp.slice(-1)[0]?.[dragIndexName] || 0);
			const startIndex = itemsProp.findIndex((q) => q[dragIndexName] === oldIndex);
			const endIndex = itemsProp.findIndex((q) => q[dragIndexName] === newIndex);
			const dragRow = itemsProp[useAsIndex ? oldIndex - minIndex : startIndex];

			if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) {
				return;
			}

			const transformElements = update(itemsProp, {
				$splice: [[useAsIndex ? oldIndex - minIndex : startIndex, 1], [useAsIndex ? newIndex - minIndex : endIndex, 0, dragRow]],
			});

			const newElements = transformElements.map((el, index) => ({
				...el,
				[dragIndexName]: useAsIndex ? index + minIndex
					: el.id === dragRow.id ? newIndex
						: el?.[dragIndexName] + (index <= endIndex && index >= startIndex ? startIndex > endIndex ? 1 : -1 : 0),
			}));

			setItems(newElements);
			const item = newElements.filter((q) => q.id === dragRow.id)?.[0];
			const oldItem = itemsProp.find((q) => q.id === dragRow.id);
			if (item && item[dragIndexName] !== oldItem[dragIndexName]) {
				if (changeSortRequest) {
					save(item, true, changeSortRequest)
						.then(() => {
							reload();
						});
				} else if (updateAfterDrag) {
					setLoading(true);
					updateAfterDrag(item, newElements)
						.finally(() => setLoading(false));
				} else if (saveAllRequest) {
					saveItems(
						[item],
						() => Promise.resolve({} as any),
					);
				} else if (!noSave) {
					throw 'need to either add saveAllRequest to ItemsProvider or add updateAfterDrag callback or set noSave = true';
				}
			}
		};

		const add = (item?: Partial<T>, useIdFromItem = false, isEdit: boolean = true) => {
			if (item && useIdFromItem && !isEdit) {
				const newItem = item as T;
				addItem(newItem);
				return newItem;
			}
			setId((id) => id - 1);
			const newId = (useIdFromItem ? item?.id : id) ?? id;
			const newItem = (item ? { ...item, id: newId } : { ...p.add?.(itemsProp), id: newId }) as T;
			const index = itemsProp.length - (itemsProp.length >= pagination.pageSize ? 1 + +isFirstPage : 0);

			const newItems = p.addedFirst
				? isFirstPage ? [newItem].concat(itemsProp) : [...itemsProp.slice(0, 1), newItem, ...itemsProp.slice(1)]
				: [
					...itemsProp.slice(0, index),
					newItem,
					...itemsProp.slice(index),
				];

			setItems(newItems);

			if (isEdit) setEdit(newItem);

			return newItem;
		};

		const handleLoad: ItemsProviderContext<T, TFilter, TResponse>['actions']['load'] = (
			data,
			...rest
		) => {
			return load(data?.pageSize ? { ...data, pageSize: data.pageSize + 2 } : data, ...rest);
		};

		return {
			state: {
				...context.state,
				items,
				pagination: handledPagination,
				advancedItems: itemsProp,
			},
			actions: {
				...context.actions,
				load: handleLoad,
				reload: handleReload,
				addItem: add,
				saveSortOrder,
			},
		};
	}, [itemsProp, pagination, loading, filters, loaders, edits, errors, error, p.add, context.state.selectedRows]);

	return (
		<ItemsContext.Provider value={value}>
			{typeof children === 'function' ? children(value) : children}
		</ItemsContext.Provider>
	);
};

/**
 * AdvancedItemsProvider component.
 *
 * will override itemsProvider 'addItem' method with implementation
 * that check filters and apply sorts before insert item
 * also load and store one item from page before current and one from page after current
 *
 * it should be used when
 *  - we need insert, update and remove item with filters applying (with inner ItemsProviderSynchronizer)
 *  - we need sort items and move items to the next or previous pages (with inner EditableTableWithSortable)
 *
 * @example AdvancedItemsProvider with ReactNode inside:
 * <AdvancedItemsProvider<T, TFilter> loadRequest="request" filterHandler={() => true}>
 *   {React.ReactNode}
 * </AdvancedItemsProvider>
 *
 * @example AdvancedItemsProvider with render function as children:
 * <AdvancedItemsProvider<T, TFilter> loadRequest="request" filterHandler={() => true}>
 *   {(context) => React.ReactNode}
 * </AdvancedItemsProvider>
 *
 * @example AdvancedItemsProvider with render callback:
 * <AdvancedItemsProvider<T, TFilter>
 *   loadRequest="request"
 *   filterHandler={() => true}
 *   render={(item, props) => SingleItem}
 * />
 *
 * @typeParam T - T Any {@link WithKey}
 * @typeParam TFilter
 * @typeParam TResponse
 * @param p - {@link AdvancedItemsProviderProps}
 * @type {React.FC<AdvancedItemsProviderProps>}
 * @returns React.ReactElement
 */
const AdvancedItemsProvider: <T extends WithKey, TFilter extends BaseParams = BaseParams, TResponse extends List<T> = List<T>>(
	p: AdvancedItemsProviderProps<T, TFilter, TResponse>
) => React.ReactElement<T> = <T extends WithDeleted, TFilter extends BaseParams = BaseParams, TResponse extends List<T> = List<T>>(
	p: AdvancedItemsProviderProps<T, TFilter, TResponse>,
) => {
	const { children } = p;

	const pagination = {
		...(p.pagination || { current: 1, pageSize: 12, total: 0 }),
		pageSize: p.pagination?.pageSize ? p.pagination.pageSize + 2 : 12,
		total: p.pagination?.total ?? p.rows?.count ?? 0,
	};

	const transformFiltersBeforeHandleUrl = (filters: TFilter) => {
		const urlFilters = defaultTransformFiltersBeforeHandleUrl({
			...filters,
			pageSize: filters.pageSize ? filters.pageSize - 2 : undefined,
			count: filters.pageSize || filters.count ? (filters.count || filters.pageSize) - 2 : undefined,
		});

		return p.transformFiltersBeforeHandleUrl ? p.transformFiltersBeforeHandleUrl(urlFilters) : urlFilters;
	};

	const searchParamsFromUrl = (location: { search: string }, prefix?: string) => {
		const params = getSearchParamsFromUrl(location, prefix);
		const pageSize = params[`${prefix || ''}pageSize`];

		return {
			...params,
			[`${prefix || ''}pageSize`]: pageSize ? pageSize + 2 : 12,
		} as TFilter;
	};

	return (
		<ItemsProvider<T, TFilter, TResponse>
			{...p}
			transformFiltersBeforeHandleUrl={transformFiltersBeforeHandleUrl}
			pagination={pagination}
			searchParamsFromUrl={searchParamsFromUrl}
		>
			<AdvancedItemsHandler<T, TFilter, TResponse>
				defaultSort={p.defaultSort}
				filterHandler={p.filterHandler}
				sortHandler={p.sortHandler}
				addedFirst={p.addedFirst}
				add={p.add}
				changeSortRequest={p.changeSortRequest}
			>
				{children}
			</AdvancedItemsHandler>
		</ItemsProvider>
	);
};

export default AdvancedItemsProvider;
